import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

export const Panel = ({ title, right, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-md border bg-card/80 shadow-[0_4px_20px_rgba(0,0,0,0.35)] fade-up ${className}`}>
    {title && (
      <header className="flex items-center justify-between gap-3 border-b px-5 py-3">
        <h3 className="font-display text-lg font-semibold uppercase tracking-wide">{title}</h3>
        {right}
      </header>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Stat = ({ label, value, tone, sub, testid }) => (
  <div className="rounded-md border bg-card/80 p-5 fade-up" data-testid={testid}>
    <div className="label">{label}</div>
    <div className={`num mt-2 text-2xl font-semibold ${tone || ""}`}>{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export const Tag = ({ children, tone = "muted", testid }) => {
  const tones = {
    up: "border-[hsl(var(--up)/0.4)] text-up bg-[hsl(var(--up)/0.08)]",
    down: "border-[hsl(var(--down)/0.4)] text-down bg-[hsl(var(--down)/0.08)]",
    gold: "border-[hsl(var(--gold)/0.4)] text-gold bg-[hsl(var(--gold)/0.08)]",
    warn: "border-[hsl(var(--warn)/0.4)] text-[hsl(var(--warn))] bg-[hsl(var(--warn)/0.08)]",
    blue: "border-primary/40 text-primary bg-primary/10",
    muted: "border-border text-muted-foreground",
  };
  return <span data-testid={testid} className={`inline-flex items-center rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tones[tone]}`}>{children}</span>;
};

export const dirTone = (d) => (d === "BUY" ? "up" : d === "SELL" ? "down" : "muted");
export const pnlClass = (v) => (v > 0 ? "text-up" : v < 0 ? "text-down" : "text-muted-foreground");

export function PageHeader({ title, sub, right }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl" data-testid="page-title">{title}</h1>
        {sub && <p className="mt-1 text-sm text-muted-foreground md:text-base">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

export function useFetch(path, deps = [], interval = 0) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get(path);
      setData(r.data);
      setError(null);
      setUpdatedAt(new Date().toISOString());
    } catch (e) {
      setError(errMsg(e));
      if (e?.response?.status !== 401) toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => {
    load();
    if (!interval) return;
    const t = setInterval(load, interval);
    return () => clearInterval(t);
  }, [load, interval]);
  return { data, loading, reload: load, setData, error, updatedAt };
}

export const Empty = ({ children, testid }) => (
  <div data-testid={testid} className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground">{children}</div>
);
